import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { hydrateMemoryFromDB, migrateLegacyLocalStorage } from './utils/cacheManager';
import LoginPage from './components/Login/LoginPage';
import MainLayout from './components/Layout/MainLayout';
import DashboardPage from './components/Dashboard/DashboardPage';
import PipelinePage from './components/Pipeline/PipelinePage';
import LeadGenAgentPage from './components/LeadGen/LeadGenAgentPage';
import PreQualifiedLeadsStandalonePage from './components/LeadGen/PreQualifiedLeadsStandalonePage';
import LeadRequestsStandalonePage from './components/LeadGen/LeadRequestsStandalonePage';
import QualifiedReadyLeadsPage from './components/LeadGen/QualifiedReadyLeadsPage';
import CallAgentPage from './components/CallAgent/CallAgentPage';
import WhatsAppAgentPage from './components/AgentPages/WhatsAppAgentPage';
import EmailAgentPage from './components/AgentPages/EmailAgentPage';
import LinkedInAgentPage from './components/AgentPages/LinkedInAgentPage';
import MeetingsPage from './components/Meetings/MeetingsPage';
import SettingsPage from './components/Settings/SettingsPage';
import InboundLeadsPage from './components/Leads/InboundLeadsPage';
import CampaignsPage from './components/Campaigns/CampaignsPage';
import CallCampaignPage from './components/Campaigns/CallCampaignPage';
import WhatsAppCampaignPage from './components/Campaigns/WhatsAppCampaignPage';
import EmailCampaignPage from './components/Campaigns/EmailCampaignPage';
import LinkedInCampaignPage from './components/Campaigns/LinkedInCampaignPage';
import OmniChannelCampaignPage from './components/Campaigns/OmniChannelCampaignPage';
import InboundCampaignPage from './components/Campaigns/InboundCampaignPage';
import SalesCoachPage from './components/SalesCoach/SalesCoachPage';
import DealViewPage from './components/Pipeline/DealViewPage';
import { GoogleOAuthCallback } from './components/OAuth/GoogleOAuthCallback';
import { LinkedInOAuthCallback } from './components/OAuth/LinkedInOAuthCallback';
import { ZoomOAuthCallback } from './components/OAuth/ZoomOAuthCallback';
import { GoogleMeetOAuthCallback } from './components/OAuth/GoogleMeetOAuthCallback';
import { MicrosoftTeamsOAuthCallback } from './components/OAuth/MicrosoftTeamsOAuthCallback';
import { MicrosoftCalendarOAuthCallback } from './components/OAuth/MicrosoftCalendarOAuthCallback';
import { GoogleEmailOAuthCallback } from './components/OAuth/GoogleEmailOAuthCallback';
import { MicrosoftEmailOAuthCallback } from './components/OAuth/MicrosoftEmailOAuthCallback';
import SetupPasswordPage from './components/Auth/SetupPasswordPage';
import AuthCallbackHandler from './components/Auth/AuthCallbackHandler';

const AuthRedirectGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hash = window.location.hash;
  const isMagicLink = hash.includes('type=magiclink');
  const isRecovery = hash.includes('type=recovery');

  if (isMagicLink || isRecovery) {
    return <Navigate to={`/setup-password${hash}`} replace />;
  }

  return <>{children}</>;
};

const CACHE_KEYS = [
  'call_campaign_leads',
  'whatsapp_campaign_leads',
  'email_campaign_leads',
  'linkedin_campaign_leads',
  'omni_campaign_leads',
  'inbound_campaign_leads',
  'inbound_source_leads',
  'meetings_booked',
];

const LEGACY_MAPPINGS = [
  { localStorage: 'call_campaign_leads_cache', cacheKey: 'call_campaign_leads' },
  { localStorage: 'whatsapp_campaign_leads_cache', cacheKey: 'whatsapp_campaign_leads' },
  { localStorage: 'email_campaign_leads_cache', cacheKey: 'email_campaign_leads' },
  { localStorage: 'linkedin_campaign_leads_cache', cacheKey: 'linkedin_campaign_leads' },
  { localStorage: 'omni_campaign_leads_cache', cacheKey: 'omni_campaign_leads' },
  { localStorage: 'inbound_campaign_leads_cache', cacheKey: 'inbound_campaign_leads' },
  { localStorage: 'inbound_source_leads_cache', cacheKey: 'inbound_source_leads' },
  { localStorage: 'meetings_booked_cache', cacheKey: 'meetings_booked' },
];

const ProtectedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  useEffect(() => {
    migrateLegacyLocalStorage(LEGACY_MAPPINGS);
    hydrateMemoryFromDB(CACHE_KEYS);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <MainLayout>
      {children}
    </MainLayout>
  );
};

function App() {
  return (
    <AuthProvider>
      <ProfileProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup-password" element={<SetupPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackHandler />} />
            <Route path="/oauth/google/callback" element={<GoogleOAuthCallback />} />
            <Route path="/oauth/linkedin/callback" element={<LinkedInOAuthCallback />} />
            <Route path="/oauth/zoom/callback" element={<ZoomOAuthCallback />} />
            <Route path="/oauth/google-meet/callback" element={<GoogleMeetOAuthCallback />} />
            <Route path="/oauth/microsoft-teams/callback" element={<MicrosoftTeamsOAuthCallback />} />
            <Route path="/oauth/microsoft-calendar/callback" element={<MicrosoftCalendarOAuthCallback />} />
            <Route path="/oauth/google-email/callback" element={<GoogleEmailOAuthCallback />} />
            <Route path="/oauth/microsoft-email/callback" element={<MicrosoftEmailOAuthCallback />} />
            <Route path="/*" element={
                <AuthRedirectGuard>
                <ProtectedLayout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/pipeline" element={<PipelinePage />} />
                    <Route path="/pipeline/:dealId" element={<DealViewPage />} />
                    <Route path="/meetings" element={<MeetingsPage />} />
                    <Route path="/ai-agents/lead-gen" element={<LeadGenAgentPage />} />
                    <Route path="/pre-qualified-leads" element={<PreQualifiedLeadsStandalonePage />} />
                    <Route path="/lead-requests" element={<LeadRequestsStandalonePage />} />
                    <Route path="/ai-agents/call-agent" element={<CallAgentPage />} />
                    <Route path="/ai-agents/whatsapp-agent" element={<WhatsAppAgentPage />} />
                    <Route path="/ai-agents/email-agent" element={<EmailAgentPage />} />
                    <Route path="/ai-agents/linkedin-agent" element={<LinkedInAgentPage />} />
                    <Route path="/leads/outbound" element={<QualifiedReadyLeadsPage />} />
                    <Route path="/leads/inbound" element={<InboundLeadsPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/campaigns/call" element={<CallCampaignPage />} />
                    <Route path="/campaigns/whatsapp" element={<WhatsAppCampaignPage />} />
                    <Route path="/campaigns/email" element={<EmailCampaignPage />} />
                    <Route path="/campaigns/linkedin" element={<LinkedInCampaignPage />} />
                    <Route path="/campaigns/omni-channel" element={<OmniChannelCampaignPage />} />
                    <Route path="/campaigns/inbound" element={<InboundCampaignPage />} />
                    <Route path="/sales-coach" element={<SalesCoachPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </ProtectedLayout>
                </AuthRedirectGuard>
              } />
            </Routes>
          </Router>
      </ProfileProvider>
    </AuthProvider>
  );
}

export default App;